/**
 * productBomSpreadsheetApiClient.ts
 *
 * Frontend API client communicating with PostgreSQL backend for Product BOM Spreadsheet View.
 */
import type {
  BomConfigRow,
  BomRevision,
  BomSpreadsheetWoodRow,
  BomSpreadsheetHardwareRow,
} from '@/features/boms/types/bomSpreadsheetTypes'
import { formatDate } from '@/lib/dateFormat'

export async function getProductBomConfigsApi(
  productId: string,
  productCode: string,
): Promise<BomConfigRow[]> {
  const res = await fetch(`/api/products/${encodeURIComponent(productId)}/bom-spreadsheet/configs`)
  if (!res.ok) {
    throw new Error(`Lỗi tải danh sách cấu hình BOM (${res.status})`)
  }
  const json = await res.json()
  return (json.configs || []).map((c: any) => ({
    id: c.id,
    code: c.code || productCode,
    colors: Array.isArray(c.colors) ? c.colors : [],
    woods: Array.isArray(c.woods) ? c.woods : [],
    assemblies: Array.isArray(c.assemblies) ? c.assemblies : [],
    status: c.status || 'Đang Cập Nhật',
  }))
}

export async function saveProductBomConfigsApi(
  productId: string,
  configs: BomConfigRow[],
): Promise<BomConfigRow[]> {
  const res = await fetch(`/api/products/${encodeURIComponent(productId)}/bom-spreadsheet/configs`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ configs }),
  })
  if (!res.ok) {
    throw new Error(`Lỗi lưu danh sách cấu hình BOM (${res.status})`)
  }
  const json = await res.json()
  return (json.configs || []).map((c: any) => ({
    id: c.id,
    code: c.code,
    colors: Array.isArray(c.colors) ? c.colors : [],
    woods: Array.isArray(c.woods) ? c.woods : [],
    assemblies: Array.isArray(c.assemblies) ? c.assemblies : [],
    status: c.status || 'Đang Cập Nhật',
  }))
}

export async function getProductBomDataApi(
  productId: string,
  configId: string,
): Promise<{ wood: BomSpreadsheetWoodRow[]; hardware: BomSpreadsheetHardwareRow[] }> {
  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/bom-spreadsheet/data/${encodeURIComponent(configId)}`,
  )
  if (!res.ok) {
    throw new Error(`Lỗi tải dữ liệu BOM (${res.status})`)
  }
  const json = await res.json()
  return {
    wood: Array.isArray(json.data?.wood) ? json.data.wood : [],
    hardware: Array.isArray(json.data?.hardware) ? json.data.hardware : [],
  }
}

export async function saveProductBomDataApi(
  productId: string,
  configId: string,
  data: { wood: BomSpreadsheetWoodRow[]; hardware: BomSpreadsheetHardwareRow[] },
): Promise<void> {
  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/bom-spreadsheet/data/${encodeURIComponent(configId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
  )
  if (!res.ok) {
    throw new Error(`Lỗi lưu dữ liệu BOM (${res.status})`)
  }
}

export async function getProductBomRevisionsApi(
  productId: string,
  configId: string,
): Promise<BomRevision[]> {
  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/bom-spreadsheet/revisions/${encodeURIComponent(configId)}`,
  )
  if (!res.ok) {
    throw new Error(`Lỗi tải lịch sử phiên bản BOM (${res.status})`)
  }
  const json = await res.json()
  return (json.revisions || []).map((r: any) => ({
    id: r.id,
    revNumber: r.rev_number,
    revLabel: r.rev_label,
    author: r.author,
    changesSummary: r.changes_summary,
    diffs: Array.isArray(r.diffs) ? r.diffs : [],
    snapshot: r.snapshot || { wood: [], hardware: [] },
    savedAt: r.created_at ? formatDate(r.created_at) : '',
    savedTime: r.created_at
      ? new Date(r.created_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
      : '',
  }))
}

export async function createProductBomRevisionApi(
  productId: string,
  configId: string,
  revision: BomRevision,
): Promise<void> {
  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/bom-spreadsheet/revisions/${encodeURIComponent(configId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: revision.id,
        revNumber: revision.revNumber,
        revLabel: revision.revLabel,
        author: revision.author,
        changesSummary: revision.changesSummary,
        diffs: revision.diffs,
        snapshot: revision.snapshot,
      }),
    },
  )
  if (!res.ok) {
    throw new Error(`Lỗi lưu phiên bản BOM (${res.status})`)
  }
}

export interface BomAggregateConfigItem {
  id: string
  product_id: string
  code: string
  colors: string[]
  woods: string[]
  assemblies: string[]
  status: string
  sort_order: number
  created_at?: string
  updated_at?: string
  wood: BomSpreadsheetWoodRow[]
  hardware: BomSpreadsheetHardwareRow[]
}

export async function getBomAggregateApi(): Promise<{ configs: BomAggregateConfigItem[] }> {
  const res = await fetch('/api/bom-aggregate/ok')
  if (!res.ok) {
    throw new Error(`Lỗi tải dữ liệu tổng hợp định mức (${res.status})`)
  }
  const json = await res.json()
  return {
    configs: Array.isArray(json.configs) ? json.configs : [],
  }
}
